import { WorkingEntries } from "./Entries.tsx";
import Ordering from "./Ordering.tsx";
import { LoadingAccountEntry } from "../components/Data.ts";
import { errorParser, fetchJSON, useAccount } from "../components/Loaders.ts";
import { parseAccountEntries } from "../components/Parsers.ts";
import { IUIStore, useUIStore } from "../components/Store.ts";
import { getRouteApi, Link, useSearch } from "@tanstack/react-router";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/shallow";

interface TagResponse {
  tag: string;
  entries: LoadingAccountEntry[];
}

const loadByTag = async (
  sessionId: string,
  tagId: string,
  ordering: string,
): Promise<TagResponse> =>
  fetchJSON<TagResponse>({
    sessionId,
    url: `/api/tag/${tagId}?ordering=${ordering}`,
  })
    .then((result) => Promise.resolve(result.data))
    .catch((err) =>
      Promise.reject(errorParser(`unread entries for ${tagId}`, err)),
    );

function Tag() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const searchParams = useSearch({ strict: false });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const name = searchParams.name as string | null;

  let tagId =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    getRouteApi("/homePathless/tag/$tagId").useParams().tagId! as string;
  if (tagId.includes("-")) {
    // second half is name
    tagId = tagId.split("-")[0];
  }
  const sessionId = useUIStore((s) => s.sessionId);
  const [ordering, setOrdering] = useState("highest");
  const tagKey = ["tag", tagId, ordering];
  const tagName = useUIStore(
    useShallow((s) => s.tagNames[tagId] || name || "loading..."),
  );
  const account = useAccount(sessionId);
  const isAdmin = (account.data?.groups ?? []).includes("admin");

  useEffect(() => {
    document.title = `Freshet: Tag - ${tagName}`;
  }, [tagName]);

  return (
    <div id="tag-tab" className="col" data-testid="tag-tab">
      <Link to="/Tags">Return to top tags list</Link>
      <br />
      <h4>
        Unread entries for <span data-testid="tag-display">{tagName}</span>
        {isAdmin && (
          <>
            &nbsp;<Link to={`/Duplicates/${tagId}`}>(duplicates)</Link>
          </>
        )}
      </h4>
      <Ordering ordering={ordering} setOrdering={setOrdering} />
      <WorkingEntries
        loadkey={tagKey}
        loader={() => loadByTag(sessionId, tagId, ordering)}
        selectFunction={(tagResponse: TagResponse) => {
          const entries = parseAccountEntries(tagResponse.entries);
          useUIStore.setState(
            produce((s: IUIStore) => {
              s.tagNames[tagId] = tagResponse.tag;
            }),
          );
          return entries;
        }}
        entriesKey={`tags-${tagId}`}
        noUnreadElement={<>No remaining unread items on this tag</>}
      />
    </div>
  );
}

export default Tag;
